import React, { useEffect, useState } from 'react'

import Spinner from '@components/shared/Spinner'
import { getXCLEARCorrelationInfo, setAccessToken } from '@api/utils'
import { useRouter } from 'next/router'
import { enrollmentApi, mfaAuthApi } from '@api/enrollmentClient/config'
import {
  ContactMethod,
  MfaChannelsChannels,
} from '@clear-denver/tpc-openapi-typescript/build/v1/enrollment/models'

import useEnrollmentApi from '@hooks/useEnrollmentApi'
import PageContent from '@components/layout/PageContent'
import { HeadTitle } from '@components/layout/HeadTitle'
import { PageHeading } from '@components/layout/Heading'
import { Card, Text, RadioGroup, InputGroup } from '@clear/design-system'
import { Row, Form, Actions, ErrorMessage } from '@components/layout/Form'
import { useForm } from 'react-hook-form'
import { ContinueButton, BackButton } from '@components/FormButtons'
import { object, string } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

export const MFA_VALIDATION = {
  INCOMPLETE: 'Please enter verification code.',
  MIN_LENGTH: 'Verification code should be at least 5 digits.',
  INCORRECT_CODE: "Code doesn't match. Try again.",
}

const defaultValues = {
  channel: ContactMethod.EMAIL,
}

const channelSchema = object({
  channel: string().required(),
})

const verificationCodeSchema = object({
  verificationCode: string()
    .required(MFA_VALIDATION.INCOMPLETE)
    .test(
      'length test',
      MFA_VALIDATION.MIN_LENGTH,
      (value: string | undefined) => {
        if (value == null) return true
        return value.length >= 5
      }
    ),
})

function MultiFactorAuth() {
  const router = useRouter()
  const { updateState } = useEnrollmentApi()
  const { register, handleSubmit, watch, formState } = useForm<
    typeof defaultValues & { global: { message: string } }
  >({
    mode: 'onChange',
    resolver: yupResolver(channelSchema),
    defaultValues,
  })

  const { errors } = formState

  const verificationCodeForm = useForm<
    { verificationCode: string } & { global: { message: string } }
  >({
    mode: 'onChange',
    resolver: yupResolver(verificationCodeSchema),
  })

  const { channel } = watch()
  const { enrollmentId = '' as string, nextRoute = '' as string } = router.query
  const [isLoading, setIsLoading] = useState(false)
  const [availableMFAChannels, setAvailableMFAChannels] = useState<
    MfaChannelsChannels[]
  >([])

  const { verificationCode } = verificationCodeForm.watch()
  const [token, setToken] = useState('')
  const [canVerify, setCanVerify] = useState(false)

  useEffect(() => {
    const getMFAChannels = async () => {
      setIsLoading(true)
      if (enrollmentId) {
        try {
          const mfa = await mfaAuthApi.channels({
            xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
            enrollmentId: enrollmentId.toString(),
          })
          setAvailableMFAChannels(mfa.channels)
        } catch (error) {
          console.log(error)
        }
      }
      setIsLoading(false)
    }
    getMFAChannels()
  }, [enrollmentId])

  async function handleChannelSubmit() {
    setIsLoading(true)
    try {
      // Make call to send notification to selected channel
      // Get mfa token and store it
      const { token } = await mfaAuthApi.challenge({
        xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
        enrollmentId: enrollmentId.toString(),
        mfaChallenge: {
          channel,
        },
      })
      setToken(token)
      setCanVerify(true)
    } catch (error) {
      console.log(error)
    }
    setIsLoading(false)
  }

  async function handleVerificationCodeSubmit() {
    setIsLoading(true)
    try {
      const mfa = await mfaAuthApi.verify({
        xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
        mfaVerify: {
          token,
          pass: verificationCode,
        },
      })
      setAccessToken(mfa.jwt)
      if (enrollmentId) {
        try {
          const enrollment = await enrollmentApi.getStateById({
            xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
            enrollmentId: enrollmentId as string,
          })
          updateState(enrollment)
        } catch (error) {
          setIsLoading(false)
          console.log(error)
        }
      }
      router.push({
        pathname: nextRoute as string,
      })
    } catch (e) {
      // setCanSubmit(false)
      console.log(e)
      if (e.status === 422) {
        verificationCodeForm.setError('verificationCode', {
          message: MFA_VALIDATION.INCORRECT_CODE,
        })
      }
    }
    setIsLoading(false)
  }

  return (
    <PageContent>
      <HeadTitle
        title="Update Contact Info | TSA&nbsp;PreCheck® Enrollment Provided by CLEAR"
        description="Update contact information on your TSA&nbsp;PreCheck® account."
      />
      <PageHeading color="brandDarkBlue">Update Contact Info</PageHeading>
      <Card mb={5} p={[2, 6]}>
        <Text
          as="h3"
          variant="display"
          fontSize={5}
          my={4}
          textAlign="center"
          color="brandDarkBlue"
        >
          {canVerify
            ? 'Enter your verification code'
            : 'Get a verification code'}
        </Text>
        {canVerify ? (
          <Form>
            <Row mb={6} mx={[null, 1]}>
              <InputGroup
                {...verificationCodeForm.register('verificationCode')}
                name="verificationCode"
                label="Verification Code"
                data-cy="verification-code"
                maxLength={6}
                color={
                  verificationCodeForm.formState.errors.verificationCode
                    ? 'default'
                    : 'error'
                }
                error={
                  verificationCodeForm.formState.errors.verificationCode
                    ?.message
                }
                containerProps={{
                  width: [null, '50%'],
                }}
              />
            </Row>

            <Actions justifyContent="space-between" mx={4} my={6}>
              <BackButton
                text="Resend Code"
                icon="refresh"
                onClick={() => setCanVerify(false)}
              />
              <ContinueButton
                text="Submit"
                data-cy="continue"
                disabled={
                  !verificationCodeForm.formState.isValid && !errors.global
                }
                onClick={verificationCodeForm.handleSubmit(
                  handleVerificationCodeSubmit
                )}
              />
            </Actions>
          </Form>
        ) : (
          <Form onSubmit={handleSubmit(handleChannelSubmit)}>
            <Row mb={2} mx={[null, 2]} data-cy="subject">
              <RadioGroup
                {...register('channel')}
                options={availableMFAChannels.map((availableChannel) => ({
                  label: `${
                    availableChannel.channel[0].toUpperCase() +
                    availableChannel.channel.slice(1).toLowerCase()
                  }: ${availableChannel.label}`,
                  value: availableChannel.channel,
                }))}
                selected={channel}
              />
            </Row>

            <Actions justifyContent="flex-end" mx={4} my={6}>
              <ContinueButton
                text="Send"
                data-cy="send-channel-notification"
                disabled={
                  !formState.isDirty && !formState.isValid && !errors.global
                }
              />
            </Actions>
          </Form>
        )}
      </Card>
      {isLoading && <Spinner />}
      {errors.global && <ErrorMessage>{errors.global.message}</ErrorMessage>}
    </PageContent>
  )
}

export default MultiFactorAuth
